import React, { useRef, useEffect } from 'react';

export default function BoxBreathingCanvas({ paused }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId;
    const startTime = performance.now();
    let accumulatedPause = 0;
    let pauseStart = null;
    let lastElapsed = 0;

    function drawFrame(elapsedMs) {
      const elapsed = elapsedMs / 1000;
      const width = canvas.width;
      const height = canvas.height;
      const cx = width / 2;
      const cy = height / 2;

      let boxSize = 136;
      if (width < 200 || height < 200) {
        boxSize = Math.min(width, height) * 0.5;
      }
      const boxLeft = cx - boxSize / 2;
      const boxTop = cy - boxSize / 2;

      const inhale = 4, hold = 4, exhale = 4, pauseDur = 4;
      const total = inhale + hold + exhale + pauseDur;
      const phaseRatio = (elapsed % total) / total;

      let lungSize = 0.7;
      let phaseText = '';
      let phaseDescription = '';
      let gradientColors = ['#37D5D6', '#36096D'];
      let progress = 0;
      
      if (phaseRatio < inhale / total) {
        progress = phaseRatio / (inhale / total);
        lungSize = 0.7 + progress * 0.3;
        phaseText = 'Inhale';
        phaseDescription = 'Breathe in slowly';
        gradientColors = ['#34C9EB', '#147EFB'];
      } else if (phaseRatio < (inhale + hold) / total) {
        progress = (phaseRatio - inhale / total) / (hold / total);
        lungSize = 1;
        phaseText = 'Hold';
        phaseDescription = 'Hold your breath';
        gradientColors = ['#147EFB', '#53D769'];
      } else if (phaseRatio < (inhale + hold + exhale) / total) {
        progress = (phaseRatio - (inhale + hold) / total) / (exhale / total);
        lungSize = 1 - progress * 0.3;
        phaseText = 'Exhale';
        phaseDescription = 'Breathe out slowly';
        gradientColors = ['#53D769', '#FC3158'];
      } else {
        progress = (phaseRatio - (inhale + hold + exhale) / total) / (pauseDur / total);
        lungSize = 0.7;
        phaseText = 'Pause';
        phaseDescription = 'Rest before next breath';
        gradientColors = ['#FC3158', '#FF9500'];
      }

      ctx.clearRect(0, 0, width, height);

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 2;
      ctx.strokeRect(boxLeft, boxTop, boxSize, boxSize);

      const perimeter = boxSize * 4;
      const d = (phaseRatio * perimeter) % perimeter;
      
      ctx.lineWidth = 3;
      ctx.strokeStyle = gradientColors[0];
      
      ctx.beginPath();
      if (d < boxSize) {
        ctx.moveTo(boxLeft, boxTop);
        ctx.lineTo(boxLeft + d, boxTop);
      } else if (d < boxSize * 2) {
        ctx.moveTo(boxLeft, boxTop);
        ctx.lineTo(boxLeft + boxSize, boxTop);
        ctx.lineTo(boxLeft + boxSize, boxTop + (d - boxSize));
      } else if (d < boxSize * 3) {
        ctx.moveTo(boxLeft, boxTop);
        ctx.lineTo(boxLeft + boxSize, boxTop);
        ctx.lineTo(boxLeft + boxSize, boxTop + boxSize);
        ctx.lineTo(boxLeft + boxSize - (d - boxSize * 2), boxTop + boxSize);
      } else {
        ctx.moveTo(boxLeft, boxTop);
        ctx.lineTo(boxLeft + boxSize, boxTop);
        ctx.lineTo(boxLeft + boxSize, boxTop + boxSize);
        ctx.lineTo(boxLeft, boxTop + boxSize);
        ctx.lineTo(boxLeft, boxTop + boxSize - (d - boxSize * 3));
      }
      ctx.stroke();

      const grad = ctx.createLinearGradient(
        cx - 25, cy - 25,
        cx + 25, cy + 25
      );
      grad.addColorStop(0, gradientColors[0]);
      grad.addColorStop(1, gradientColors[1]);
      ctx.fillStyle = grad;

      const lungScale = boxSize / 136;

      function drawLung(offsetX) {
        ctx.beginPath();
        ctx.moveTo(cx + offsetX * lungScale, cy);
        ctx.bezierCurveTo(
          cx + offsetX * lungScale * lungSize * 2,
          cy - 50 * lungScale * lungSize,
          cx + offsetX * lungScale * 80 * lungSize / 15,
          cy + 70 * lungScale * lungSize,
          cx + offsetX * lungScale * lungSize,
          cy + 60 * lungScale * lungSize
        );
        ctx.bezierCurveTo(
          cx + offsetX * lungScale * lungSize / 1.5,
          cy + 55 * lungScale * lungSize,
          cx + offsetX * lungScale * lungSize / 3,
          cy + 20 * lungScale * lungSize,
          cx + offsetX * lungScale,
          cy
        );
        ctx.closePath();
        ctx.fill();
      }
      
      drawLung(-15);
      drawLung(15);

      ctx.strokeStyle = grad;
      ctx.lineWidth = 5 * lungScale;
      ctx.beginPath();
      ctx.moveTo(cx, cy - 45 * lungScale);
      ctx.lineTo(cx, cy - 10 * lungScale);
      ctx.stroke();

      ctx.lineWidth = 4 * lungScale;
      ctx.beginPath();
      ctx.moveTo(cx, cy - 10 * lungScale);
      ctx.lineTo(cx - 11 * lungScale, cy + 6 * lungScale);
      ctx.moveTo(cx, cy - 10 * lungScale);
      ctx.lineTo(cx + 11 * lungScale, cy + 6 * lungScale);
      ctx.stroke();

      ctx.fillStyle = 'white';
      ctx.font = `bold ${16 * lungScale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
      const textWidth = ctx.measureText(phaseText).width;
      ctx.fillText(phaseText, cx - textWidth / 2, boxTop - 16 * lungScale);
      
      ctx.font = `${12 * lungScale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
      const descWidth = ctx.measureText(phaseDescription).width;
      ctx.fillText(phaseDescription, cx - descWidth / 2, boxTop + boxSize + 20 * lungScale);
    }

    const draw = () => {
      const now = performance.now();
      
      if (paused) {
        if (pauseStart === null) pauseStart = now;
      } else {
        if (pauseStart !== null) {
          accumulatedPause += now - pauseStart;
          pauseStart = null;
        }
      }
      
      let elapsed = now - startTime - accumulatedPause;
      if (paused) elapsed = lastElapsed;
      else lastElapsed = elapsed;

      drawFrame(elapsed);
      animationFrameId = requestAnimationFrame(draw);
    };

    const resize = () => {
      const w = window.innerWidth;
      const canvasSize =
        w <= 576  ? 240 :
        w <= 768  ? 280 :
        w <= 992  ? 320 :
        w <= 1200 ? 360 :
                    400;
      
      canvas.width = canvas.height = canvasSize;
      drawFrame(lastElapsed);
    };

    window.addEventListener('resize', resize);
    resize();
    draw();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resize);
    };
  }, [paused]);

  return (
    <canvas
      ref={canvasRef}
      className="breathing-canvas"
    />
  );
}